import { apiService } from './api.service.js'
import { cacheService } from './cache.service.js'
import { AUTH_CONSTANTS } from '../constants/auth.constants.js'

/**
 * Serviço de cache para categorias com cache-first e respostas enriquecidas
 * Implementa padrão cache-first com TTL de 10 minutos,
 * deduplicação de requisições simultâneas, processamento de
 * respostas enriquecidas do backend (derivados.categorias, derivados.orcamentoCriado,
 * derivados.orcamentosDesativados, derivados.orcamentosRemovidos)
 * e fallback offline.
 *
 * Chave de cache: categorias
 * Eventos disparados: categorias-cache-updated
 *
 * Derivados processados:
 * - derivados.categorias: lista completa de categorias ativas (substituição integral)
 * - derivados.orcamentoCriado: orçamento criado automaticamente (tipo "Despesa")
 * - derivados.orcamentosDesativados: IDs de orçamentos desativados (mudança para "Receita")
 * - derivados.orcamentosRemovidos: IDs de orçamentos removidos (exclusão de categoria)
 */
export class CategoriaCacheService {
  constructor() {
    this.cacheKey = 'categorias'
    this.endpoint = 'categorias'
    /** Promise em andamento para deduplicação de requisições simultâneas */
    this.loadingPromise = null
  }

  /**
   * Lista categorias com cache-first (TTL de 10 minutos)
   * Usa cacheService.isValid() para verificação centralizada de TTL
   * Implementa deduplicação via loadingPromise
   * Implementa fallback offline retornando cache expirado quando sem conexão
   *
   * @returns {Promise<Array>} Lista de categorias ativas
   */
  async listar() {
    // Cache-first: verifica validade com TTL padrão de 10 minutos
    if (cacheService.isValid(this.cacheKey)) {
      return cacheService.get(this.cacheKey) || []
    }

    // Fallback offline: retorna cache expirado quando sem conexão
    if (!navigator.onLine) {
      const cacheExpirado = cacheService.get(this.cacheKey)
      return cacheExpirado || []
    }

    // Deduplicação: reutiliza Promise em andamento
    if (this.loadingPromise) {
      return this.loadingPromise
    }

    // Cache miss: busca do backend
    this.loadingPromise = this._loadFromBackend()

    try {
      const result = await this.loadingPromise
      return result
    } finally {
      this.loadingPromise = null
    }
  }

  /**
   * Carrega categorias do backend via GET.
   * Backend retorna: { success: true, data: [...] } (retrocompatibilidade).
   * Em caso de timeout de autenticação (AUTH_TIMEOUT), retorna dados do cache local
   * ou array vazio — sem propagar exceção e sem executar requisição à API.
   *
   * @returns {Promise<Array>} Lista de categorias
   * @private
   */
  async _loadFromBackend () {
    try {
      const response = await apiService.get(this.endpoint)
      const categorias = response.data?.data || response.data || response

      cacheService.set(this.cacheKey, categorias)
      return categorias || []
    } catch (error) {
      // Em timeout de auth, retorna cache local ou array vazio (Req 5.2)
      if (error.code === AUTH_CONSTANTS.AUTH_TIMEOUT_ERROR) {
        const cacheLocal = cacheService.get(this.cacheKey)
        return cacheLocal || []
      }
      throw error
    }
  }

  /**
   * Cria nova categoria no backend e atualiza o cache local
   * Processa resposta enriquecida: { success: true, data: { entidade, derivados, avisos } }
   * - derivados.categorias substitui integralmente o cache de categorias
   * - derivados.orcamentoCriado adiciona orçamento ao cache (quando tipo "Despesa")
   * Dispara evento categorias-cache-updated
   *
   * @param {Object} dadosCategoria - Dados da categoria a ser criada
   * @returns {Promise<Object>} Categoria criada pelo backend
   */
  async criar(dadosCategoria) {
    const response = await apiService.post(this.endpoint, dadosCategoria)
    const responseData = response.data || response

    // Extrai do envelope enriquecido { entidade, derivados, avisos }
    const entidade = responseData.entidade || responseData
    const derivados = responseData.derivados || {}

    // Processa derivados (categorias, orcamentoCriado)
    this._processarDerivados(derivados)

    // Se derivados.categorias não veio, adiciona ao cache manualmente
    if (!derivados.categorias) {
      cacheService.addToArray(this.cacheKey, entidade)
    }

    // Dispara evento de atualização para UI
    window.dispatchEvent(new CustomEvent('categorias-cache-updated'))

    return entidade
  }

  /**
   * Atualiza categoria existente no backend e sincroniza o cache local
   * Processa resposta enriquecida:
   * - derivados.categorias substitui integralmente o cache
   * - derivados.orcamentoCriado adiciona orçamento (mudança Receita→Despesa)
   * - derivados.orcamentosDesativados remove orçamentos (mudança Despesa→Receita)
   * Dispara evento categorias-cache-updated
   *
   * @param {string} id - ID da categoria a ser atualizada
   * @param {Object} dadosAtualizacao - Dados atualizados da categoria
   * @returns {Promise<Object>} Categoria atualizada pelo backend
   */
  async atualizar(id, dadosAtualizacao) {
    const response = await apiService.put(`${this.endpoint}/${id}`, dadosAtualizacao)
    const responseData = response.data || response

    // Extrai do envelope enriquecido { entidade, derivados, avisos }
    const entidade = responseData.entidade || responseData
    const derivados = responseData.derivados || {}

    // Processa derivados (categorias, orcamentoCriado, orcamentosDesativados)
    this._processarDerivados(derivados)

    // Se derivados.categorias não veio, atualiza no cache manualmente
    if (!derivados.categorias) {
      cacheService.updateInArray(this.cacheKey, id, entidade)
    }

    // Dispara evento de atualização para UI
    window.dispatchEvent(new CustomEvent('categorias-cache-updated'))

    return entidade
  }

  /**
   * Exclui categoria do backend e remove do cache local
   * Processa resposta enriquecida:
   * - derivados.categorias substitui integralmente o cache
   * - derivados.orcamentosRemovidos remove orçamentos associados do cache
   * Dispara evento categorias-cache-updated
   *
   * @param {string} id - ID da categoria a ser excluída
   * @returns {Promise<boolean>} True se excluída com sucesso
   */
  async excluir(id) {
    const response = await apiService.delete(`${this.endpoint}/${id}`)
    const responseData = response.data || response

    // Extrai derivados da resposta enriquecida
    const derivados = responseData.derivados || {}

    // Processa derivados (categorias, orcamentosRemovidos)
    this._processarDerivados(derivados)

    // Se derivados.categorias não veio, remove do cache manualmente
    if (!derivados.categorias) {
      cacheService.removeFromArray(this.cacheKey, id)
    }

    // Dispara evento de atualização para UI
    window.dispatchEvent(new CustomEvent('categorias-cache-updated'))

    return true
  }

  /**
   * Processa entidades derivadas da resposta enriquecida do backend
   * - derivados.categorias: substitui integralmente o cache de categorias (lista completa ordenada)
   * - derivados.orcamentoCriado: adiciona orçamento ao cache de orçamentos do período
   * - derivados.orcamentosDesativados: remove orçamentos desativados do cache por ID
   * - derivados.orcamentosRemovidos: remove orçamentos removidos do cache por ID
   *
   * @param {Object} derivados - Objeto com entidades derivadas do backend
   * @param {Array} [derivados.categorias] - Lista completa de categorias ativas (substituição integral)
   * @param {Object} [derivados.orcamentoCriado] - Orçamento criado automaticamente (tipo "Despesa")
   * @param {Array<string>} [derivados.orcamentosDesativados] - IDs de orçamentos desativados
   * @param {Array<string>} [derivados.orcamentosRemovidos] - IDs de orçamentos removidos
   * @private
   */
  _processarDerivados(derivados) {
    if (!derivados || Object.keys(derivados).length === 0) return

    // Substitui integralmente o cache de categorias com a lista completa do backend
    if (derivados.categorias) {
      cacheService.set(this.cacheKey, derivados.categorias)
    }

    // Adiciona orçamento criado automaticamente ao cache de orçamentos
    if (derivados.orcamentoCriado) {
      const orcamento = derivados.orcamentoCriado
      const periodo = orcamento.periodo // formato: AAAA-MM
      if (periodo) {
        const [ano, mes] = periodo.split('-')
        const cacheKey = `orcamentos_${mes}_${ano}`
        cacheService.addToArray(cacheKey, orcamento)
        window.dispatchEvent(new CustomEvent('orcamentos-cache-updated'))
      }
    }

    // Remove orçamentos desativados do cache (mudança Despesa→Receita)
    if (derivados.orcamentosDesativados && derivados.orcamentosDesativados.length > 0) {
      this._removerOrcamentosDoCache(derivados.orcamentosDesativados)
    }

    // Remove orçamentos removidos do cache (exclusão de categoria)
    if (derivados.orcamentosRemovidos && derivados.orcamentosRemovidos.length > 0) {
      this._removerOrcamentosDoCache(derivados.orcamentosRemovidos)
    }
  }

  /**
   * Remove orçamentos do cache de orçamentos por ID
   * Percorre todos os períodos cacheados para encontrar e remover os orçamentos
   *
   * @param {Array<string>} orcamentoIds - IDs dos orçamentos a remover
   * @private
   */
  _removerOrcamentosDoCache(orcamentoIds) {
    // Obtém mês/ano corrente como período padrão para busca
    const hoje = new Date()
    const mes = String(hoje.getMonth() + 1).padStart(2, '0')
    const ano = String(hoje.getFullYear())
    const cacheKey = `orcamentos_${mes}_${ano}`

    // Remove cada orçamento do cache do período corrente
    orcamentoIds.forEach(id => {
      cacheService.removeFromArray(cacheKey, id)
    })
  }

  /**
   * Busca categoria por ID no cache local
   * @param {string} id - ID da categoria
   * @returns {Object|null} Categoria encontrada ou null
   */
  buscarPorId(id) {
    const categorias = cacheService.get(this.cacheKey) || []
    return categorias.find(categoria => categoria.id === id) || null
  }

  /**
   * Força recarregamento do cache de categorias
   * Remove cache existente e busca dados atualizados do backend
   * @returns {Promise<Array>} Lista atualizada de categorias
   */
  async recarregar() {
    cacheService.remove(this.cacheKey)
    return this.listar()
  }

  /**
   * Inicializa cache no startup do sistema
   * Só busca do backend se não tem cache válido
   * @returns {Promise<void>}
   */
  async inicializarCache() {
    try {
      if (!cacheService.isValid(this.cacheKey)) {
        await this._loadFromBackend()
      }
    } catch (error) {
      console.error('❌ Erro ao inicializar cache de categorias:', error)
    }
  }

  /**
   * Limpa cache de categorias
   */
  limparCache() {
    cacheService.remove(this.cacheKey)
  }
}

// Instância singleton
export const categoriaCacheService = new CategoriaCacheService()
